import { useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getCurrentUser, login } from "@/services/authService";
import { normalizeAuthUser } from "@/lib/authUser";

type LoginErrorPayload = {
  message?: string;
  errors?: {
    email?: string[];
    password?: string[];
    [key: string]: string[] | undefined;
  };
};

/** Remember email on this device (password is never stored). Server session uses the same checkbox via `remember`. */
const LOGIN_REMEMBER_EMAIL_KEY = "inventory_login_remember_email";
const LOGIN_REMEMBER_ME_KEY = "inventory_login_remember_me";

function readStoredRememberEmail(): string {
  try {
    if (localStorage.getItem(LOGIN_REMEMBER_ME_KEY) !== "1") return "";
    return localStorage.getItem(LOGIN_REMEMBER_EMAIL_KEY)?.trim() ?? "";
  } catch {
    return "";
  }
}

function readStoredRememberMe(): boolean {
  try {
    return localStorage.getItem(LOGIN_REMEMBER_ME_KEY) === "1";
  } catch {
    return false;
  }
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(readStoredRememberMe);
  const [email, setEmail] = useState(readStoredRememberEmail);
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      const loginResponse = await login({ email, password, remember: rememberMe });
      console.log("[Login] Success response", { payload: loginResponse });
      const currentUserResponse = await getCurrentUser();
      console.log("[Login] Current user response", { payload: currentUserResponse });
      const user = normalizeAuthUser(currentUserResponse);
      if (user) {
        localStorage.setItem("auth_user", JSON.stringify(user));
      }
      if (rememberMe) {
        try {
          localStorage.setItem(LOGIN_REMEMBER_EMAIL_KEY, email.trim());
          localStorage.setItem(LOGIN_REMEMBER_ME_KEY, "1");
        } catch {
          /* ignore quota / private mode */
        }
      } else {
        try {
          localStorage.removeItem(LOGIN_REMEMBER_EMAIL_KEY);
          localStorage.removeItem(LOGIN_REMEMBER_ME_KEY);
        } catch {
          /* ignore */
        }
      }
      navigate("/dashboard");
    } catch (error) {
      if (isAxiosError(error)) {
        const errorPayload = error.response?.data as LoginErrorPayload | undefined;
        console.log("[Login] Error response", { status: error.response?.status, payload: errorPayload });
        setFormError(errorPayload?.message || "Login failed. Please try again.");
      } else {
        console.error("[Login] Network/API error", error);
        setFormError("Unable to connect to server. Please check your API link.");
      }
    } finally {
      setIsSubmitting(false);
    }

  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-background">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-primary/3 blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md mx-4 relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
            <Car className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Auto<span className="gradient-text">Vault</span>
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">Car Inventory Management System</p>
        </div>

        {/* Login Card */}
        <div className="glass-card p-8">
          <h2 className="text-xl font-display font-semibold text-foreground mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-6">Sign in to manage your inventory</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {formError ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="glass-input pl-10 h-11"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-muted-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glass-input pl-10 pr-10 h-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setRememberMe(next);
                    if (!next) {
                      try {
                        localStorage.removeItem(LOGIN_REMEMBER_EMAIL_KEY);
                        localStorage.removeItem(LOGIN_REMEMBER_ME_KEY);
                      } catch {
                        /* ignore */
                      }
                    }
                  }}
                  className="rounded border-border bg-muted"
                />
                Remember me
              </label>
              <Link to="#" className="text-sm text-primary hover:text-primary/80 transition-colors">
                Forgot password?
              </Link>
            </div>

            <Button type="submit" variant="premium" className="w-full h-11" disabled={isSubmitting}>
              {isSubmitting ? "Signing In..." : "Sign In"}
            </Button>
          </form>

          {/* <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/register" className="text-primary hover:text-primary/80 font-medium transition-colors">
                Create account
              </Link>
            </p>
          </div> */}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
